import { useEffect, useState } from "react";
import { Link2, Unlink, Crop as CropIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import ImageEditorModal from "./ImageEditorModal";
import { ImagePresetsRow } from "./ImagePresetsRow";

const SHADOW_PRESETS = {
  none: "",
  soft: "0 2px 8px rgba(0,0,0,0.12)",
  medium: "0 6px 16px rgba(0,0,0,0.18)",
  strong: "0 12px 28px rgba(0,0,0,0.3)",
};

const matchShadowPreset = (node) => node.getAttribute("data-shadow") || "none";

/**
 * Live-editing panel for an <img> selected inside the TinyMCE editor.
 * Every change is applied straight to the DOM node (instant preview) and
 * the editor's change event is fired so the parent's content state stays
 * in sync. Crop/rotate/flip is delegated to the existing ImageEditorModal.
 */
export function ImageStylePanel({ open, onClose, editor, node }) {
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [lockAspect, setLockAspect] = useState(true);
  const [radius, setRadius] = useState(0);
  const [shadow, setShadow] = useState("none");
  const [showCrop, setShowCrop] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  useEffect(() => {
    if (!open || !node) return;
    const w = node.getAttribute("width") || node.style.width || node.offsetWidth || node.naturalWidth || "";
    const h = node.getAttribute("height") || node.style.height || node.offsetHeight || node.naturalHeight || "";
    const wpx = parseInt(w, 10) || "";
    const hpx = parseInt(h, 10) || "";
    setWidth(wpx);
    setHeight(hpx);
    setAspectRatio(wpx && hpx ? wpx / hpx : (node.naturalWidth && node.naturalHeight ? node.naturalWidth / node.naturalHeight : 1));
    setRadius(parseInt(node.style.borderRadius, 10) || 0);
    setShadow(matchShadowPreset(node));
  }, [open, node]);

  if (!node) return null;

  const apply = (styles) => {
    if (!editor || !node) return;
    editor.dom.setStyles(node, styles);
    editor.fire("change");
  };

  const handleWidthChange = (val) => {
    const w = val === "" ? "" : parseInt(val, 10) || 0;
    setWidth(w);
    let h = height;
    if (lockAspect && w) {
      h = Math.round(w / aspectRatio);
      setHeight(h);
    }
    node.removeAttribute("width");
    node.removeAttribute("height");
    apply({ width: w ? `${w}px` : "", height: h ? `${h}px` : "" });
  };

  const handleHeightChange = (val) => {
    const h = val === "" ? "" : parseInt(val, 10) || 0;
    setHeight(h);
    let w = width;
    if (lockAspect && h) {
      w = Math.round(h * aspectRatio);
      setWidth(w);
    }
    node.removeAttribute("width");
    node.removeAttribute("height");
    apply({ width: w ? `${w}px` : "", height: h ? `${h}px` : "" });
  };

  const handleRadiusChange = ([val]) => {
    setRadius(val);
    apply({ "border-radius": val ? `${val}px` : "" });
  };

  const handleShadowChange = (key) => {
    setShadow(key);
    apply({ "box-shadow": SHADOW_PRESETS[key] });
    editor.dom.setAttrib(node, "data-shadow", key === "none" ? null : key);
  };

  const applyPreset = (presetRadius, presetShadow) => {
    setRadius(presetRadius);
    setShadow(presetShadow);
    apply({ "border-radius": presetRadius ? `${presetRadius}px` : "", "box-shadow": SHADOW_PRESETS[presetShadow] });
    editor.dom.setAttrib(node, "data-shadow", presetShadow === "none" ? null : presetShadow);
  };

  const handleCropSaved = (newUrl) => {
    editor.dom.setAttribs(node, { src: newUrl, "data-mce-src": newUrl });
    editor.fire("change");
    setShowCrop(false);
  };

  return (
    <>
      <Dialog open={open && !showCrop} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-md rounded-none max-h-[85vh] overflow-y-auto" data-testid="image-style-panel">
          <DialogHeader>
            <DialogTitle>Image Style</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label>Size (px)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number" placeholder="Width" value={width}
                  onChange={(e) => handleWidthChange(e.target.value)}
                  className="rounded-none" data-testid="image-style-width-input"
                />
                <Button
                  type="button" size="icon" variant={lockAspect ? "default" : "outline"}
                  className="rounded-none shrink-0" onClick={() => setLockAspect((v) => !v)}
                  title={lockAspect ? "Aspect ratio locked" : "Aspect ratio unlocked"}
                  data-testid="image-style-lock-aspect-btn"
                >
                  {lockAspect ? <Link2 size={14} /> : <Unlink size={14} />}
                </Button>
                <Input
                  type="number" placeholder="Height" value={height}
                  onChange={(e) => handleHeightChange(e.target.value)}
                  className="rounded-none" data-testid="image-style-height-input"
                />
              </div>
            </div>

            <ImagePresetsRow radius={radius} shadow={shadow} onApply={applyPreset} testIdPrefix="image-style-preset" />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Corner Radius</Label>
                <span className="text-xs text-muted-foreground" data-testid="image-style-radius-value">{radius}px</span>
              </div>
              <Slider
                value={[radius]} min={0} max={80} step={1}
                onValueChange={handleRadiusChange} data-testid="image-style-radius-slider"
              />
            </div>

            <div className="space-y-2">
              <Label>Shadow</Label>
              <div className="flex gap-2">
                {Object.keys(SHADOW_PRESETS).map((key) => (
                  <Button
                    key={key} type="button" size="sm"
                    variant={shadow === key ? "default" : "outline"}
                    className="rounded-none capitalize flex-1"
                    onClick={() => handleShadowChange(key)}
                    data-testid={`image-style-shadow-${key}`}
                  >
                    {key}
                  </Button>
                ))}
              </div>
            </div>

            <Button
              type="button" variant="outline" className="w-full rounded-none"
              onClick={() => setShowCrop(true)} data-testid="image-style-crop-btn"
            >
              <CropIcon size={14} className="mr-2" /> Crop / Rotate / Flip...
            </Button>
          </div>

          <DialogFooter>
            <Button onClick={onClose} className="rounded-none" data-testid="image-style-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageEditorModal
        open={showCrop}
        onClose={() => setShowCrop(false)}
        sourceUrl={node.getAttribute("src")}
        onSaved={handleCropSaved}
      />
    </>
  );
}

export default ImageStylePanel;
